// src/ui/Photo.tsx (T1): a square-cornered 4:3 photo, and the provenance badge, the only thing that ever sits on
// a photo (Direction D rule 8). Put flags, "New" and people in the text under the photo. Root classes: hb-photo,
// hb-src-badge.
import type { ComponentChildren } from "preact";
import { Link } from "../app/router";
import { Avatar, type AvatarTone } from "./Avatar";
import { cx } from "./cx";

export type PhotoSource =
  | { kind: "builder"; mark: string; label: ComponentChildren }                       // "MA" + "The Hillside Manor"
  | { kind: "person"; initials: string; tone: AvatarTone; label: ComponentChildren };  // avatar + "Priya"

export function SourceBadge(props: { source: PhotoSource }) {
  const s = props.source;
  return (
    <span class="hb-src-badge src-badge">
      {s.kind === "builder" ? (
        <span class="mk" aria-hidden="true">
          {s.mark}
        </span>
      ) : (
        <Avatar initials={s.initials} tone={s.tone} size="xs" />
      )}
      {s.label}
    </span>
  );
}

export function Photo(props: {
  src: string | null;
  /** Alt text: the AI caption or title for photos, "" for decorative images (spec 2.9). */
  alt: string;
  source?: PhotoSource;
  /** Makes the photo a link (app path) or a button. */
  to?: string;
  onClick?: () => void;
  /** Accessible name for a linked or clickable photo when alt is empty. */
  label?: string;
  ratio?: "4/3" | "1/1" | "16/9";
  eager?: boolean;
  class?: string;
  children?: ComponentChildren;
}) {
  const style = props.ratio && props.ratio !== "4/3" ? { aspectRatio: props.ratio.replace("/", " / ") } : undefined;
  const cls = cx("hb-photo", "ph", props.class);
  const inner = (
    <>
      {props.src ? <img src={props.src} alt={props.alt} loading={props.eager ? "eager" : "lazy"} decoding="async" /> : null}
      {props.source ? <SourceBadge source={props.source} /> : null}
      {props.children}
    </>
  );
  if (props.to) {
    return (
      <Link to={props.to} class={cls} style={style} aria-label={props.label}>
        {inner}
      </Link>
    );
  }
  if (props.onClick) {
    return (
      <button type="button" class={cx(cls, "hb-photo-btn")} style={style} onClick={props.onClick} aria-label={props.label}>
        {inner}
      </button>
    );
  }
  return (
    <div class={cls} style={style}>
      {inner}
    </div>
  );
}
